/**
 * Which cars are the reader's, and what the next one costs (SPEC-010).
 *
 * The catalogue is generous and that generosity is what this module takes back.
 * Every car was handed over on the first scroll, which made a deck of two dozen
 * legends worth about as much as a contact sheet: the schoolyard version of this
 * product had scarcity in it, and the reason a Yellowbird card mattered was that
 * the boy across the table did not have one.
 *
 * So twelve cars are open and the rest are a LADDER. Each rung is a margin over
 * the reader's own best score — a few hundred points, which is a handful of
 * rounds — which means the price of the next car is quoted against a bar the
 * reader keeps raising themselves. There is nothing to buy, nothing to spend and
 * no second chance for money; the only input is playing better than you played
 * before.
 *
 * Everything here is pure and every function takes the catalogue as it stands.
 * Nothing in this module knows where the won ids are KEPT — that is
 * `application/game/collection.js`, and the split is the usual one: the rules
 * here, the storage there, in the same way `duelRun` never learns that a device
 * board exists.
 */

/**
 * The cars every reader starts with.
 *
 * Twelve because twelve is the brief the catalogue was authored to — the
 * records after it were added later — and because it is the number that
 * leaves the duel a real game on the first visit. `buildDuelPool` over twelve
 * cars and eleven subjects is 726 candidate pairings before filtering, which is
 * a run of unrepeated questions longer than anybody plays in one sitting.
 *
 * It is deliberately not a share of the catalogue, and the second art drop is
 * why that matters: ten cars arrived at once, and a rule like "the top fifty per
 * cent" would have handed five of them over on the day they landed while a rule
 * like "the last two" would have relocked eight cars readers had already won.
 * The constant stays put and the LADDER grows instead.
 */
export const OPEN_CAR_COUNT = 12;

/**
 * The thirteen rungs, as MARGINS over the bar standing when a run opens.
 *
 * They climb because the bar climbs with them: a reader who wins their first car
 * at 400 points has just set 400 as the bar, so the second car is asked for at
 * 750 rather than at 350. Quoting the ladder in margins rather than in absolute
 * scores is what makes it mean the same thing to somebody on their fourth run as
 * to somebody on their fortieth.
 *
 * THE RUNGS ARE SIZED IN ROUNDS, and that is the arithmetic the first version of
 * this list did not do. A round pays between `BASE_ROUND_POINTS` and
 * `MAX_ROUND_POINTS` — 100 and 300 — and ONE WRONG ANSWER ENDS THE RUN, so a
 * margin is not a budget a reader saves up across sessions. It is a number of
 * consecutive correct answers, in a single sitting, against a clock that is at
 * its five-second floor from round eleven. The ladder opened at 1,000 and ran to
 * 60,000, and compounding made that far worse than it looks: the fifth car was
 * already a hundred perfect rounds in a row and the twelfth was over a thousand,
 * which is not a hard target but an unreachable one.
 *
 * So the rungs are quoted at TWO TO THREE rounds of margin each, and the climb
 * comes from the BAR rather than from the margin: every car won raises the bar
 * the next one is measured against, so the twelfth still asks for a run of
 * nineteen perfect rounds — around thirty for a reader who stays under two
 * seconds and takes the double rather than the triple — where the first asks
 * for two.
 *
 * The step is 25 points and it widens to 50 over the last four rungs, which is
 * where the ladder stops being about learning the catalogue and starts being
 * about knowing it.
 *
 * TWO ROUNDS IS THE FLOOR FOR THE FIRST RUNG and it is a floor on purpose. A
 * margin under `MAX_ROUND_POINTS` would hand a car to the first question of a
 * reader's first run, which is a ceremony nobody has been made to want yet — the
 * gap on the scoreboard has to be read at least once before it is closed. That
 * floor is what moved this list last: the speed bonus doubled (SPEC-007 D-73),
 * so a perfect round pays 300 rather than 200, and margins that stood still
 * would have quietly put the first car inside one lucky tap.
 *
 * There is one more rung than there are cars to win, which is the state this
 * ladder has always been in and it is harmless: `prizeForRun` stops when the
 * locked list empties, not when the margins do. A ladder SHORTER than the prize
 * list is the bug, because it leaves a car in the garage that no score can
 * reach.
 *
 * REPRICING A LADDER MOVES A TARGET SOMEBODY WAS CHASING, and every time this
 * list has moved it has moved every one of them DOWNWARDS — including the last
 * time, when the numbers went UP: a rung is priced in rounds, and a round that
 * now pays half again as much makes every one of these cheaper to reach than the
 * list it replaced. That direction is the
 * reason it is safe to do at all: a reader mid-climb finds the next car cheaper
 * than it was, never dearer, and a reader who has already won cars keeps every
 * one of them — the won ids are stored, and nothing here re-derives them from a
 * score.
 */
export const UNLOCK_MARGINS = [
    325, 350, 375, 400, 425, 450, 475,
    500, 525, 575, 625, 675, 725,
    // The unicorn drop (SPEC-016 PR-16.4). Six cars means six more locked ones —
    // thirty in the pack, twelve open, eighteen on the ladder — and thirteen
    // rungs would have left five of them behind a price no score could reach.
    // Appended at the step the list already runs at, never respaced: PR-10.9's
    // D-20 is the rule, and it is the same one every time this list has moved.
    // The climb still comes from the BAR rather than from the margin, so the
    // eighteenth rung is a serious run without the number itself being large.
    775, 825, 875, 925, 975, 1025,
];

/** An id list that arrived from storage, reduced to the ones we can act on. */
function knownIds(cars, wonIds) {
    const catalogue = new Set((Array.isArray(cars) ? cars : []).map((car) => car?.id));

    return (Array.isArray(wonIds) ? wonIds : []).filter((id) => catalogue.has(id));
}

/**
 * The collection, in four lists, in the CONTENT PACK's own order.
 *
 * That order is `cars.js` as it is authored — the F40 first because it is the
 * deck's showcase, the two most recent records last — and it is deliberately not
 * the garage's. The garage sorts by year and then by name (`sortCars`), which is
 * a READING order: it makes the shelf a walk from 1984 to 1994. Locking the tail
 * of a reading order would make a car's prize status an accident of when it
 * happened to be built, and the reader would be able to see that it was an
 * accident.
 *
 * Four lists rather than a predicate per car, because every caller wants a whole
 * list: the duel needs the pool, the garage needs both shelves, and the ceremony
 * needs the next locked one. A `isCarUnlocked(car, wonIds)` called once per car
 * would rebuild the same set once per car and would still leave the ORDER for
 * each caller to get right on its own.
 *
 * SO `cars` MUST ARRIVE IN THE CONTENT PACK'S ORDER, and that is a contract
 * rather than a preference: this function takes the first twelve of whatever
 * array it is handed, so a caller that sorted first would lock a different two
 * cars and nothing anywhere would say so. `useCarCatalogue` answers the fetched
 * order and every caller passes it straight through — the garage sorts AFTER the
 * split, not before it. `carCollection.test.js` pins the trap.
 */
export function splitCollection(cars, wonIds) {
    const catalogue = Array.isArray(cars) ? cars : [];
    const won = new Set(knownIds(catalogue, wonIds));
    const open = catalogue.slice(0, OPEN_CAR_COUNT);
    const beyond = catalogue.slice(OPEN_CAR_COUNT);

    return {
        open,
        won: beyond.filter((car) => won.has(car.id)),
        locked: beyond.filter((car) => !won.has(car.id)),
        unlocked: [...open, ...beyond.filter((car) => won.has(car.id))],
    };
}

/**
 * The car the next rung pays out, or null where there is not one.
 *
 * The FIRST still-locked car in catalogue order, which is what makes the ladder
 * a sequence rather than a lottery: a reader who has been shown a silhouette in
 * the garage and then wins something else would read the ceremony as a
 * consolation prize.
 */
export function nextLockedCar(cars, wonIds) {
    return splitCollection(cars, wonIds).locked[0] ?? null;
}

/**
 * The score that wins the next car, or null where no score can.
 *
 * Null covers two different endings and neither is a failure: the ladder is
 * spent — every rung has been climbed — or the catalogue has nothing left to
 * give.
 * Both are states a screen draws nothing for, which is why they answer the same
 * way rather than throwing or returning Infinity.
 *
 * `record` is the BAR the run is being measured against, and it moves in exactly
 * one place: `claimRun` sets it to the score that just bought a car. Everywhere
 * else it is the device's best score as the run OPENED, never as it stands — a
 * target measured against a record the current run has already moved is a target
 * that runs away from the reader as they chase it. A claim is the exception that
 * proves it: the score it re-bases on has been PAID for a car, so the reader is
 * never asked to buy the same points twice.
 */
export function unlockTarget(record, wonCount) {
    const margin = UNLOCK_MARGINS[Math.max(0, Math.trunc(wonCount) || 0)];

    if (margin === undefined) {
        return null;
    }

    return Math.max(0, Math.trunc(record) || 0) + margin;
}

/**
 * The car this run has bought, or null.
 *
 * AT MOST ONE PER CALL, and it is an invariant rather than a rule that had to be
 * enforced: a run that clears rung *n* becomes the new bar by definition — it
 * beat the old one by the whole of that rung's margin — and rung *n+1* is then
 * measured from that new bar plus a margin the same score cannot also have
 * cleared. The arithmetic is stated here anyway, because a reader of this
 * function should not have to re-derive it before trusting that one enormous
 * first run does not empty the garage in one go.
 *
 * WHICH IS HOW A RUN WINS SEVERAL (SPEC-010 PR-10.11) and why this function did
 * not have to change to allow it: the reader takes the car mid-run, `claimRun`
 * re-bases the bar on the score that bought it, and the next call is asked a
 * question about a rung nothing has paid for yet. The ladder is climbed one rung
 * at a time whether the rungs are a fortnight apart or ninety seconds.
 *
 * A run worth nothing wins nothing even where the record is zero and the margin
 * would technically be met by a zero. `score > 0` is not defensive: `expireRun`
 * banks a run that timed out on round one, and that run has genuinely scored
 * nothing.
 */
export function prizeForRun({ cars, wonIds, record, score }) {
    const { won, locked } = splitCollection(cars, wonIds);
    const target = unlockTarget(record, won.length);
    const points = Math.max(0, Math.trunc(score) || 0);

    if (target === null || locked.length === 0 || points <= 0 || points < target) {
        return null;
    }

    return locked[0];
}

/**
 * How far short of the next car this run fell, or null where the question does
 * not apply.
 *
 * Its own function rather than a subtraction at the call site, because the two
 * places that ask — the duel's scoreboard while a run is live, and the near-miss
 * line after one ends — must agree about what happens at the edges. Zero is
 * never returned: a gap of nothing is a car that was won, and the near-miss line
 * would be telling a winner they were nought points short.
 */
export function pointsShort({ cars, wonIds, record, score }) {
    const { won, locked } = splitCollection(cars, wonIds);
    const target = unlockTarget(record, won.length);

    if (target === null || locked.length === 0) {
        return null;
    }

    const gap = target - Math.max(0, Math.trunc(score) || 0);

    return gap > 0 ? gap : null;
}
